import { useEffect, useMemo } from "react";
import {
  ArrowRight,
  Car,
  CheckCircle2,
  Gift,
  PhoneCall,
  Quote,
  Sparkles,
  UserCheck,
  Wallet,
} from "lucide-react";
import { PUBLIC_SITE_URL } from "@/config/site";
import { ROUTE_PATHS } from "@/routes/routePaths";
import {
  CorporateConnectForm,
  SectionEyebrow,
} from "../components/CorporateConnectForm";
import {
  BENEFIT_ACCENTS,
  BENEFIT_ICONS,
  CORPORATE_CARPOOL_SEO,
  CORPORATE_COMPANIES,
  CORPORATE_JSON_LD,
  HELP_ICONS,
  HERO_CHIPS,
  corporateCarpoolCopy,
  type SavingPlan,
} from "../data/corporateCarpoolContent";
import "../styles/corporateCarpool.css";

function SavingCard({ plan, variant }: { plan: SavingPlan; variant: "host" | "guest" }) {
  const RoleIcon = variant === "host" ? Car : UserCheck;
  const breakdownRows = plan.rows.slice(0, -1);
  const totalRow = plan.rows[plan.rows.length - 1];

  return (
    <div className={`corporate-saving-card ${variant}`}>
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="corporate-role-badge">
          <RoleIcon className="h-4 w-4" />
          {plan.role}
        </span>
      </div>
      <p className="mt-2 text-sm text-gray-500">{plan.tagline}</p>

      <div className="corporate-saving-hero">
        <p className="text-xs font-medium uppercase tracking-wide text-white/80">
          {plan.monthlyLabel}
        </p>
        <p className="mt-1 text-4xl font-extrabold">{plan.monthly}</p>
        <p className="mt-1 text-sm text-white/90">{plan.yearly}</p>
      </div>

      <div>
        {breakdownRows.map((row) => (
          <div key={row.detail} className="corporate-saving-row">
            <span className="text-gray-600">{row.detail}</span>
            <span className="whitespace-nowrap font-semibold text-gray-900">
              {row.amount}
            </span>
          </div>
        ))}
      </div>

      <div className="corporate-saving-total">
        <span>{totalRow.detail}</span>
        <span className="whitespace-nowrap">{totalRow.amount}</span>
      </div>
    </div>
  );
}

/**
 * Free Corporate Carpool marketing page — site header/footer come from Layout.
 */
export default function CorporateCarpoolPage() {
  const t = corporateCarpoolCopy;
  const benefitCards = [t.employeeBenefits, t.employerBenefits, t.cityBenefits];
  const carouselCompanies = useMemo(
    () => [...CORPORATE_COMPANIES, ...CORPORATE_COMPANIES],
    []
  );

  useEffect(() => {
    const prevTitle = document.title;
    document.title = CORPORATE_CARPOOL_SEO.title;

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? "";
    if (meta) meta.setAttribute("content", CORPORATE_CARPOOL_SEO.description);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "corporate-carpool-jsonld";
    script.text = JSON.stringify([
      ...CORPORATE_JSON_LD,
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${PUBLIC_SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Corporate Carpool",
            item: `${PUBLIC_SITE_URL}${ROUTE_PATHS.CORPORATE_CARPOOL}`,
          },
        ],
      },
    ]);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
      script.remove();
    };
  }, []);

  return (
    <div className="corporate-page min-h-screen font-sans text-gray-800">
      <section className="corporate-hero px-5 pb-16 pt-12 text-white sm:pb-20 sm:pt-16">
        <div className="relative mx-auto max-w-5xl text-center">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-4 py-1.5 text-xs font-medium tracking-wide backdrop-blur-sm sm:text-sm">
            <Sparkles className="h-4 w-4" />
            {t.heroBadge}
          </span>
          <h1 className="mb-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            {t.heroTitle}
          </h1>
          <p className="mb-3 text-lg font-medium text-green-50 sm:text-2xl">
            {t.heroSubtitle}
          </p>
          <p className="mx-auto mb-8 max-w-2xl text-sm text-green-100/90 sm:text-base">
            {t.heroTagline}
          </p>

          <div className="mb-9 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
            {HERO_CHIPS.map((chip) => {
              const ChipIcon = chip.icon;
              return (
                <span key={chip.label} className="corporate-hero-chip">
                  <ChipIcon className="h-4 w-4" />
                  {chip.label}
                </span>
              );
            })}
          </div>

          <a href="#cp-connect" className="corporate-hero-cta">
            {t.heroCta}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-14 px-4 pb-16 pt-12 sm:space-y-20 sm:px-6">
        <section>
          <div className="mb-9 text-center">
            <SectionEyebrow icon={Sparkles}>{t.whyEyebrow}</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-bold text-gray-900 sm:text-3xl">
              {t.whyTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {benefitCards.map((card, index) => {
              const Icon = BENEFIT_ICONS[index];
              const accent = BENEFIT_ACCENTS[index];
              return (
                <div key={card.title} className="corporate-benefit-card">
                  <div
                    className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} shadow-md`}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="mb-3 text-lg font-bold text-gray-900">{card.title}</h3>
                  {card.dualStats ? (
                    <div className="mb-4 grid grid-cols-2 gap-3">
                      {card.dualStats.map((s) => (
                        <div
                          key={s.role}
                          className="rounded-xl border border-green-100 bg-green-50 px-3 py-2.5"
                        >
                          <p className="text-xs font-semibold text-green-700">{s.role}</p>
                          <p className="mt-0.5 text-xl font-bold text-green-700">{s.value}</p>
                          <p className="mt-0.5 text-[11px] leading-tight text-gray-500">
                            {s.sub}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="mb-1 text-3xl font-bold text-green-700">{card.stat}</div>
                      <p className="mb-4 text-sm text-gray-500">{card.statLabel}</p>
                    </>
                  )}
                  <ul className="space-y-2">
                    {card.benefits.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="mb-9 text-center">
            <SectionEyebrow icon={Wallet}>{t.hostGuest.badge}</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-bold text-gray-900 sm:text-3xl">
              {t.hostGuest.title}
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-gray-600">
              {t.hostGuest.description}
            </p>
          </div>

          <div className="corporate-section-card mb-8 border-l-4 border-l-green-500">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-900">
              <Car className="h-4 w-4 text-green-600" />
              {t.hostGuest.assumptionsTitle}
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {t.hostGuest.assumptions.map((item) => (
                <div key={item} className="corporate-impact-item text-sm text-gray-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <SavingCard plan={t.hostGuest.host} variant="host" />
            <SavingCard plan={t.hostGuest.guest} variant="guest" />
          </div>
          <p className="mt-5 text-center text-xs text-gray-400">{t.hostGuest.footnote}</p>
        </section>

        <section className="corporate-section-card">
          <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
            {t.impact.title}
          </h2>
          <p className="mb-5 text-gray-600">{t.impact.description}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {t.impact.items.map((item) => (
              <div key={item} className="corporate-impact-item text-sm text-gray-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-9 text-center">
            <SectionEyebrow icon={PhoneCall}>{t.howWeHelp.badge}</SectionEyebrow>
            <h2 className="mt-3 text-2xl font-bold text-gray-900 sm:text-3xl">
              {t.howWeHelp.title}
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-gray-600">
              {t.howWeHelp.description}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {t.howWeHelp.steps.map((step, index) => {
              const Icon = HELP_ICONS[index] ?? HELP_ICONS[0];
              return (
                <div key={step.title} className="corporate-help-card">
                  <span className="corporate-help-number">{index + 1}</span>
                  <div className="corporate-help-icon">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-gray-900">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-600">{step.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="corporate-section-card">
          <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">
            {t.carousel.title}
          </h2>
          <p className="mb-5 text-gray-600">{t.carousel.description}</p>
          <div className="corporate-carousel-wrap py-2">
            <div className="corporate-carousel-track">
              {carouselCompanies.map((company, index) => (
                <div key={`${company}-${index}`} className="corporate-company-badge">
                  {company}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="corporate-section-card border-l-4 border-l-green-500">
          <h2 className="mb-4 text-xl font-bold text-gray-900 sm:text-2xl">
            {t.gratitude.title}
          </h2>
          <div className="space-y-3 leading-relaxed text-gray-600">
            {t.gratitude.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          <div className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
              <Gift className="h-5 w-5 text-green-600" />
              {t.gratitude.programTitle}
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {t.gratitude.programItems.map((item) => (
                <div key={item} className="corporate-program-item text-sm text-gray-700">
                  <Sparkles className="h-4 w-4 shrink-0 text-amber-500" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="corporate-closing">
          <div className="relative z-10 max-w-3xl">
            <SectionEyebrow icon={Quote}>{t.closing.badge}</SectionEyebrow>
            <p className="mb-6 mt-4 text-xl font-semibold leading-relaxed sm:text-2xl">
              &ldquo;{t.closing.quote}&rdquo;
            </p>
            <div className="space-y-3 text-sm leading-relaxed text-green-50/90 sm:text-base">
              {t.closing.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <div className="mt-7 border-t border-white/20 pt-6">
              <p className="text-sm text-green-100/80">{t.closing.signOff}</p>
              <p className="mt-1 text-lg font-bold text-white">{t.closing.team}</p>
            </div>
          </div>
        </section>

        <CorporateConnectForm />
      </div>
    </div>
  );
}
